<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import { getInboxIconByType } from 'dashboard/helper/inbox';

import CardLayout from 'dashboard/components-next/CardLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import LiveChatCampaignDetails from './LiveChatCampaignDetails.vue';
import SMSCampaignDetails from './SMSCampaignDetails.vue';

const props = defineProps({
  title: {
    type: String,
    default: '',
  },
  message: {
    type: String,
    default: '',
  },
  isLiveChatType: {
    type: Boolean,
    default: false,
  },
  isEnabled: {
    type: Boolean,
    default: false,
  },
  status: {
    type: String,
    default: '',
  },
  sender: {
    type: Object,
    default: null,
  },
  inbox: {
    type: Object,
    default: null,
  },
  scheduledAt: {
    type: Number,
    default: 0,
  },
  showAnalytics: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['edit', 'delete', 'analytics']);

const { t } = useI18n();

const STATUS_COMPLETED = 'completed';
const STATUS_PROCESSING = 'processing';

const { formatMessage } = useMessageFormatter();

const isActive = computed(() =>
  props.isLiveChatType ? props.isEnabled : props.status !== STATUS_COMPLETED
);

const statusTextColor = computed(() => ({
  'text-auth-action dark:text-auth-accent': isActive.value,
  'text-auth-muted dark:text-auth-muted-dark': !isActive.value,
}));

const campaignStatus = computed(() => {
  if (props.isLiveChatType) {
    return props.isEnabled
      ? t('CAMPAIGN.LIVE_CHAT.CARD.STATUS.ENABLED')
      : t('CAMPAIGN.LIVE_CHAT.CARD.STATUS.DISABLED');
  }

  if (props.status === STATUS_COMPLETED) {
    return t('CAMPAIGN.SMS.CARD.STATUS.COMPLETED');
  }

  if (props.status === STATUS_PROCESSING) {
    return t('CAMPAIGN.SMS.CARD.STATUS.PROCESSING');
  }

  return t('CAMPAIGN.SMS.CARD.STATUS.SCHEDULED');
});

const inboxName = computed(() => props.inbox?.name || '');

const inboxIcon = computed(() => {
  const {
    medium,
    channel_type: type,
    voice_enabled: voiceEnabled,
  } = props.inbox;
  return getInboxIconByType(type, medium, 'fill', voiceEnabled);
});
</script>

<template>
  <CardLayout
    layout="row"
    class="!rounded-none !bg-transparent !outline-none transition-colors hover:!bg-auth-soft/70 focus-within:!bg-auth-soft/70 dark:hover:!bg-auth-soft-dark/70 dark:focus-within:!bg-auth-soft-dark/70 [&>div:first-child]:!items-start [&>div:first-child]:!gap-4 [&>div:first-child]:!px-4 [&>div:first-child]:!py-4 sm:[&>div:first-child]:!items-center sm:[&>div:first-child]:!px-5 sm:[&>div:first-child]:!py-5"
  >
    <div class="flex min-w-0 flex-1 flex-col items-start gap-2.5">
      <div
        class="flex w-full min-w-0 flex-col items-start gap-2 sm:flex-row sm:items-center sm:gap-3"
      >
        <span
          class="line-clamp-1 min-w-0 text-sm font-semibold capitalize text-auth-text dark:text-auth-text-dark sm:text-base"
        >
          {{ title }}
        </span>
        <span
          class="inline-flex h-6 shrink-0 items-center rounded-md border border-auth-border bg-auth-soft px-2 py-0.5 text-xs font-medium dark:border-auth-border-dark dark:bg-auth-soft-dark"
          :class="statusTextColor"
        >
          {{ campaignStatus }}
        </span>
      </div>
      <div
        v-dompurify-html="formatMessage(message, false, false, false)"
        class="line-clamp-1 min-h-5 text-sm text-auth-muted dark:text-auth-muted-dark [&>p]:mb-0"
      />
      <div
        class="flex min-h-6 w-full items-center gap-2 overflow-hidden text-auth-muted dark:text-auth-muted-dark"
      >
        <LiveChatCampaignDetails
          v-if="isLiveChatType"
          :sender="sender"
          :inbox-name="inboxName"
          :inbox-icon="inboxIcon"
        />
        <SMSCampaignDetails
          v-else
          :inbox-name="inboxName"
          :inbox-icon="inboxIcon"
          :scheduled-at="scheduledAt"
        />
      </div>
    </div>
    <div class="flex shrink-0 items-center justify-end gap-2">
      <Button
        v-if="showAnalytics"
        v-tooltip.top="t('CAMPAIGN.WHATSAPP.CARD.ANALYTICS')"
        variant="faded"
        size="sm"
        color="slate"
        icon="i-lucide-chart-no-axes-column"
        class="!rounded-lg !border !border-auth-border !bg-auth-soft !text-auth-text hover:!bg-auth-border dark:!border-auth-border-dark dark:!bg-auth-soft-dark dark:!text-auth-text-dark dark:hover:!bg-auth-border-dark"
        :title="t('CAMPAIGN.WHATSAPP.CARD.ANALYTICS')"
        @click="emit('analytics')"
      />
      <Button
        v-if="isLiveChatType"
        variant="faded"
        size="sm"
        color="slate"
        icon="i-lucide-sliders-vertical"
        class="!rounded-lg !border !border-auth-border !bg-auth-soft !text-auth-text hover:!bg-auth-border dark:!border-auth-border-dark dark:!bg-auth-soft-dark dark:!text-auth-text-dark dark:hover:!bg-auth-border-dark"
        @click="emit('edit')"
      />
      <Button
        variant="faded"
        color="ruby"
        size="sm"
        icon="i-lucide-trash"
        class="!rounded-lg"
        @click="emit('delete')"
      />
    </div>
  </CardLayout>
</template>
